import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import * as PortOne from '@portone/browser-sdk/v2';
import { useCart } from '../context/CartContext';
import api from '../api';
import { getSocket } from '../socket';
import SplitPayment from '../components/SplitPayment';
import LoadingSpinner from '../components/LoadingSpinner';
import BottomNav from '../components/BottomNav';

const PORTONE_STORE_ID = process.env.REACT_APP_PORTONE_STORE_ID || 'store-713c5ef0-791d-4fd4-98c4-d0b904dd5c24';
const CHANNEL_KEYS = {
  card: process.env.REACT_APP_PORTONE_CHANNEL_CARD || 'channel-key-710661d0-8f74-4a8a-90ac-5fa0fcbbdbba',
  tosspay: process.env.REACT_APP_PORTONE_CHANNEL_TOSSPAY || 'channel-key-710661d0-8f74-4a8a-90ac-5fa0fcbbdbba',
  kakaopay: process.env.REACT_APP_PORTONE_CHANNEL_KAKAOPAY || 'channel-key-45b39692-958b-47f4-a2d0-367971349930',
};

const PAYMENT_METHODS = [
  {
    id: 'card',
    label: '신용카드',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="4" width="22" height="16" rx="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
  },
  {
    id: 'kakaopay',
    label: '카카오페이',
    icon: (
      <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
        <text x="0" y="12" fontSize="12" fontWeight="800" fontFamily="var(--font)">kakao</text>
      </svg>
    ),
  },
  {
    id: 'tosspay',
    label: '토스페이',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <text x="4" y="18" fontSize="18" fontWeight="800" fontFamily="var(--font)">T</text>
      </svg>
    ),
  },
];

const METHOD_STYLES = {
  card: { bg: 'var(--border)', color: 'var(--text)' },
  kakaopay: { bg: '#FEE500', color: '#3A1D1D' },
  tosspay: { bg: '#0064FF', color: '#fff' },
};

export default function PaymentPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { storeId, tableId } = useCart();

  const splitAmountParam = searchParams.get('split');
  const splitAmount = splitAmountParam ? Number(splitAmountParam) : null;

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [splitOpen, setSplitOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('card');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [toast, setToast] = useState('');
  const toastTimer = useRef(null);

  useEffect(() => {
    return () => clearTimeout(toastTimer.current);
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 3000);
  };

  const fetchSession = useCallback(async () => {
    try {
      const res = await api.get(`/sessions/${sessionId}/summary`);
      const result = res?.data || res;
      if (result?.session) {
        const s = result.session;
        setSession({
          tableNumber: s.table_number,
          tableId: s.table_id,
          storeId: s.store_id,
          totalAmount: s.total_amount || 0,
          paidAmount: s.paid_amount || 0,
          orders: (result.orders || []).map((o) => ({
            ...o,
            items: (o.items || []).map((item) => ({
              ...item,
              name: item.menu_name || item.name || '메뉴',
              price: item.unit_price || item.price || 0,
            })),
          })),
        });
      }
    } catch {}
    finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  useEffect(() => {
    if (!storeId) return;
    let socket;
    try {
      socket = getSocket(storeId);
      socket.on('payment_update', (data) => {
        if (data?.sessionId === sessionId) fetchSession();
      });
    } catch {}
    return () => { if (socket) socket.off('payment_update'); };
  }, [storeId, sessionId, fetchSession]);

  const handlePayAmount = async (amount) => {
    setPaymentLoading(true);
    try {
      const initiateRes = await api.post('/payments/initiate', {
        session_id: sessionId,
        amount,
        method: selectedMethod,
        payer_info: { device: navigator.userAgent.slice(0, 50) },
      });
      const paymentId = initiateRes?.payment_id || initiateRes?.data?.payment_id;
      if (!paymentId) throw new Error('결제 초기화 실패');

      localStorage.setItem('portone_payment_ctx', JSON.stringify({
        paymentId,
        sessionId,
        storeId: session?.storeId || storeId,
        tableId: session?.tableId || tableId,
        tableNumber: session?.tableNumber,
        amount,
      }));

      const base = {
        storeId: PORTONE_STORE_ID,
        paymentId,
        orderName: `테이블 ${session?.tableNumber || ''}번 주문`,
        totalAmount: amount,
        currency: 'CURRENCY_KRW',
        redirectUrl: `${window.location.origin}/payment/success`,
      };

      let paymentRequest;
      if (selectedMethod === 'kakaopay') {
        paymentRequest = {
          ...base,
          channelKey: CHANNEL_KEYS.kakaopay,
          payMethod: 'EASY_PAY',
          easyPay: { easyPayProvider: 'EASY_PAY_PROVIDER_KAKAOPAY' },
        };
      } else if (selectedMethod === 'tosspay') {
        paymentRequest = {
          ...base,
          channelKey: CHANNEL_KEYS.tosspay,
          payMethod: 'EASY_PAY',
          easyPay: { easyPayProvider: 'EASY_PAY_PROVIDER_TOSSPAY' },
        };
      } else {
        paymentRequest = {
          ...base,
          channelKey: CHANNEL_KEYS.card,
          payMethod: 'CARD',
        };
      }

      const response = await PortOne.requestPayment(paymentRequest);

      if (response?.code) {
        localStorage.removeItem('portone_payment_ctx');
        showToast(response.message || '결제가 취소되었습니다.');
        setPaymentLoading(false);
        return;
      }

      if (response?.paymentId) {
        await api.post('/payments/portone-confirm', {
          payment_id: response.paymentId,
          portone_tx_id: response.txId,
          amount,
        });
        localStorage.removeItem('portone_payment_ctx');
        navigate('/payment/success?popup=1', {
          state: { amount, storeId: session?.storeId || storeId, tableId: session?.tableId || tableId, tableNumber: session?.tableNumber },
        });
      }
    } catch (err) {
      showToast(err?.message || '결제에 실패했습니다.');
      setPaymentLoading(false);
    }
  };

  if (loading) return <LoadingSpinner fullPage />;
  if (!session) return null;

  const total = session.totalAmount || 0;
  const paidAmount = session.paidAmount || 0;
  const remaining = Math.max(0, total - paidAmount);
  const paidPercent = total > 0 ? Math.min(100, (paidAmount / total) * 100) : 0;
  const allItems = (session.orders || []).flatMap((o) => o.items || []);

  const isDutchPay = splitAmount !== null && splitAmount > 0;
  const payAmount = isDutchPay ? Math.min(splitAmount, remaining) : remaining;

  const handleFullPayment = () => handlePayAmount(payAmount);

  return (
    <div className="page" style={{ paddingBottom: 'calc(60px + var(--safe-bottom))' }}>
      <header className="page-header">
        <div style={{ width: 36 }} />
        <span className="page-header-title">셀프 정산</span>
        <div style={{ width: 36 }} />
      </header>

      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 160 }}>
        {/* Dutch pay indicator */}
        {isDutchPay && (
          <div style={{
            background: 'var(--primary-light)',
            margin: '16px 20px 0',
            borderRadius: 'var(--radius-md)',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)' }}>
                더치페이 - {splitAmount.toLocaleString()}원 / {total.toLocaleString()}원
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                남은 총액에서 분할 금액만큼 결제합니다
              </div>
            </div>
          </div>
        )}

        {/* Receipt */}
        <div style={{ padding: '16px 20px' }}>
          <div className="section-title">영수증</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {allItems.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 15 }}>
                <span style={{ color: 'var(--text)' }}>{item.name} <span style={{ color: 'var(--text-tertiary)' }}>x{item.quantity}</span></span>
                <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>{(item.subtotal || (item.price || 0) * item.quantity).toLocaleString()}원</span>
              </div>
            ))}
          </div>
          <div className="divider" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 14 }}>주문 합계</span>
              <span style={{ fontWeight: 600, fontSize: 15 }}>{total.toLocaleString()}원</span>
            </div>
            {paidAmount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--success)', fontSize: 14, fontWeight: 600 }}>결제 완료</span>
                <span style={{ color: 'var(--success)', fontWeight: 700 }}>-{paidAmount.toLocaleString()}원</span>
              </div>
            )}
            <div className="divider" style={{ margin: '4px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>
                {isDutchPay ? '이번 결제 금액' : '남은 금액'}
              </span>
              <span className="animate-scale-in" style={{ fontSize: 24, fontWeight: 800, color: payAmount > 0 ? 'var(--primary)' : 'var(--success)', letterSpacing: '-0.5px', display: 'inline-block' }}>
                {payAmount.toLocaleString()}원
              </span>
            </div>
          </div>
          {paidAmount > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 6 }}>
                <span>결제 진행률</span><span>{Math.round(paidPercent)}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${paidPercent}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* Section divider */}
        <div style={{ height: 8, background: 'var(--border)' }} />

        {/* Payment method */}
        <div style={{ padding: '16px 20px' }}>
          <div className="section-title">결제 수단</div>
          <div className="stagger-children" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {PAYMENT_METHODS.map((method, idx) => {
              const isSelected = selectedMethod === method.id;
              const mStyle = METHOD_STYLES[method.id];
              return (
                <button
                  key={method.id}
                  className="animate-fade-in-up"
                  onClick={() => setSelectedMethod(method.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '14px 16px',
                    border: isSelected ? '2px solid var(--primary)' : '1.5px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    background: isSelected ? 'var(--primary-light)' : 'var(--surface)',
                    cursor: 'pointer', fontFamily: 'var(--font)', textAlign: 'left', width: '100%',
                    transition: 'all 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                    animationDelay: `${idx * 50}ms`,
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 'var(--radius-sm)',
                    background: mStyle.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: mStyle.color, flexShrink: 0,
                  }}>
                    {method.icon}
                  </div>
                  <span style={{ fontWeight: 600, fontSize: 15, color: isSelected ? 'var(--primary)' : 'var(--text)' }}>
                    {method.label}
                  </span>
                  {isSelected && (
                    <div style={{ marginLeft: 'auto', color: 'var(--primary)' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Split payment (only when not in dutch pay mode) */}
        {!isDutchPay && splitOpen && (
          <div style={{ padding: '0 20px 16px' }}>
            <div style={{
              background: 'var(--surface)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-sm)',
              padding: 20,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div className="section-title" style={{ margin: 0 }}>분할 결제</div>
                <button onClick={() => setSplitOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <SplitPayment totalAmount={total} paidAmount={paidAmount} onPayAmount={(amount) => handlePayAmount(amount)} />
            </div>
          </div>
        )}
      </div>

      {/* Bottom action bar */}
      <div className="bottom-bar" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button
          className="btn btn-primary btn-block btn-lg"
          onClick={handleFullPayment}
          disabled={paymentLoading || payAmount <= 0}
        >
          {paymentLoading ? (
            <>
              <span className="spinner spinner-sm" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff', marginRight: 8 }} />
              처리 중...
            </>
          ) : `${payAmount.toLocaleString()}원 결제하기`}
        </button>
        {!isDutchPay && (
          <button className="btn btn-secondary btn-block" onClick={() => setSplitOpen(!splitOpen)} disabled={paymentLoading || remaining <= 0}>
            {splitOpen ? '분할 결제 닫기' : '분할 결제'}
          </button>
        )}
      </div>

      {toast && <div className="toast">{toast}</div>}
      <BottomNav />
    </div>
  );
}
